import moment, { invalid } from 'moment';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Grid,
    Stack,
    Paper,
    useMediaQuery,
    useTheme,
    Box,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    Popover,
    ButtonBase,
    Button,
    SwipeableDrawer,
    Divider,
} from '@mui/material';
import { Clear } from '@mui/icons-material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SearchIcon from '@mui/icons-material/Search';
import HomeIcon from '@mui/icons-material/Home';
import BusinessIcon from '@mui/icons-material/Business';
import SailingIcon from '@mui/icons-material/Sailing';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import LocalActivityIcon from '@mui/icons-material/LocalActivity';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import {
    selectEndDate,
    selectStartDate,
} from '../../../../app/Slices/DateFilterSlice';
import {
    ExperienceFilters,
    selectExperienceFilters,
    setExperienceFilters,
} from '../../../../app/Slices/ExperienceFiltersSlice';
import UserRole from '../../../../shared/AuthRoles';
import ExperiencesListAllComponent from '../../../components/Experiences/ExperiencesListAllComponent';
import DateFilter from '../../../shared/DateFilter';
import FiltersModal from '../../../shared/FiltersModal';
import VisitTypeModal from './VisitTypeModal';
import AccessDeniedModal from '../../../shared/AccessDeniedModal';
import AddressSearch from './AddressSearch';
import { CustomIconButton } from '../../../../layout/styledcomponents/CustomIconButton';
import { sevenDayApi } from '../../../../app/Slices/SevenDayApiSlice';

const getCss = () => `
    .rdrDayNumber span {
        font-weight: 400;
    }
`;

type VisitType = 'business' | 'personal' | 'all';

interface MobileFilterSectionProps {
    icon: React.ReactNode;
    iconColor?: string;
    label: string;
    value: string;
    children?: React.ReactNode;
    onClick?: () => void;
}

function MobileFilterSection({
    icon,
    iconColor,
    label,
    value,
    children,
    onClick,
}: MobileFilterSectionProps) {
    const theme = useTheme();
    return (
        <Box
            sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                p: 1.5,
                mb: 1.5,
                bgcolor: 'background.paper',
            }}
        >
            <ButtonBase
                onClick={onClick}
                disableRipple={!onClick}
                sx={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    textAlign: 'left',
                    cursor: onClick ? 'pointer' : 'default',
                }}
            >
                <Box
                    sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        bgcolor: 'action.hover',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: iconColor ?? 'text.secondary',
                        flexShrink: 0,
                    }}
                >
                    {icon}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        variant='subtitle1'
                        sx={{ fontWeight: 600, lineHeight: 1.2 }}
                    >
                        {label}
                    </Typography>
                    <Typography
                        variant='body2'
                        sx={{
                            color: 'text.secondary',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {value}
                    </Typography>
                </Box>
                <ChevronRightIcon
                    fontSize='small'
                    sx={{ color: 'text.secondary' }}
                />
            </ButtonBase>
            {children ? <Box sx={{ mt: 1.25 }}>{children}</Box> : null}
        </Box>
    );
}

export const MODULE_TYPE_CHIPS = [
    {
        value: 'property',
        label: 'Property',
        icon: <HomeIcon fontSize='small' />,
    },
    {
        value: 'commercial',
        label: 'Commercial',
        icon: <BusinessIcon fontSize='small' />,
    },
    { value: 'boat', label: 'Boat', icon: <SailingIcon fontSize='small' /> },
    {
        value: 'activity',
        label: 'Activity',
        icon: <LocalActivityIcon fontSize='small' />,
    },
    {
        value: 'vehicle',
        label: 'Vehicle',
        icon: <DirectionsCarIcon fontSize='small' />,
    },
] as const;

function withModuleTypeUpdate(
    filters: ExperienceFilters,
    type: string[],
): ExperienceFilters {
    const wasVehicle = filters.type?.[0] === 'vehicle';
    const leavingVehicle = wasVehicle && type[0] !== 'vehicle';

    if (!leavingVehicle || filters.bounds) {
        return { ...filters, type };
    }

    return {
        ...filters,
        type,
        swLat: undefined,
        swLng: undefined,
        neLat: undefined,
        neLng: undefined,
    };
}

export default function ViewAllExperiencesPage() {
    const theme = useTheme();
    const isDownMd = useMediaQuery(theme.breakpoints.down('md'));
    const isDownLg = useMediaQuery(theme.breakpoints.down('lg'));
    const loggedInUser = UserRole();
    const [openVisitModal, setVisitTypeOpen] = useState(false);
    const [activeVisitType, setActiveVisitType] = useState<
        VisitType | undefined
    >(
        () =>
            (localStorage.getItem('visitTypePref') as VisitType | null) ??
            undefined,
    );
    const isMobile = isDownMd;
    const isIpad = isDownLg;
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const experienceFilters = useSelector(selectExperienceFilters);
    const startDate = useSelector(selectStartDate);
    const endDate = useSelector(selectEndDate);

    const [typeAnchorEl, setTypeAnchorEl] = useState<HTMLElement | null>(null);
    const typePopoverOpen = Boolean(typeAnchorEl);
    const activeModuleType = experienceFilters.type?.[0] ?? '';
    const activeModuleTypeLabel =
        MODULE_TYPE_CHIPS.find((c) => c.value === activeModuleType)?.label ??
        'Select experiences';
    const isWishlistOnly = experienceFilters.wishlist;

    const [mobileSheetOpen, setMobileSheetOpen] = useState(false);
    const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        const match = location.pathname.match(
            /^\/viewExperiences\/(business|personal|all)/,
        );
        const routeType = (match?.[1] as VisitType | undefined) ?? undefined;
        const saved =
            (localStorage.getItem('visitTypePref') as VisitType | null) ?? null;
        const seen = localStorage.getItem('visitTypeSeen') === '1';

        if (routeType === 'all' && saved) {
            setActiveVisitType(saved);
            if (!location.pathname.startsWith(`/viewExperiences/${saved}`)) {
                navigate(`/viewExperiences/${saved}`, { replace: true });
            }
        } else if (routeType && saved && routeType !== saved) {
            setActiveVisitType(saved);
            navigate(`/viewExperiences/${saved}`, { replace: true });
        } else if (routeType) {
            setActiveVisitType(routeType);
        } else if (saved) {
            setActiveVisitType(saved);
            if (!location.pathname.startsWith(`/viewExperiences/${saved}`)) {
                navigate(`/viewExperiences/${saved}`, { replace: true });
            }
        }

        if (!seen && !saved) setVisitTypeOpen(true);
        if (!seen) sessionStorage.setItem('visitTypeSeen', '1');
    }, [location.pathname, navigate]);

    const handleCloseVisitModal = () => setVisitTypeOpen(false);

    useEffect(() => {
        if (startDate && endDate && startDate <= endDate) {
            dispatch(
                setExperienceFilters({
                    ...experienceFilters,
                    checkinDate: moment(startDate).format('YYYY-MM-DD'),
                    checkoutDate: moment(endDate).format('YYYY-MM-DD'),
                }),
            );
            return;
        }
        if (startDate === undefined && endDate === undefined) {
            dispatch(
                setExperienceFilters({
                    ...experienceFilters,
                    checkinDate: '',
                    checkoutDate: '',
                }),
            );
        }
    }, [startDate, endDate]);

    const handleAddress = (bounds: google.maps.LatLngBounds, state: string) => {
        dispatch(
            setExperienceFilters({
                ...experienceFilters,
                state: [state],
                bounds,
                swLat: bounds.getSouthWest().lat(),
                swLng: bounds.getSouthWest().lng(),
                neLat: bounds.getNorthEast().lat(),
                neLng: bounds.getNorthEast().lng(),
            }),
        );
    };

    const handleToggleModuleType = (
        _: React.MouseEvent<HTMLElement>,
        value: string | null,
    ) => {
        const updated = value ? [value] : [];
        dispatch(
            setExperienceFilters(
                withModuleTypeUpdate(experienceFilters, updated),
            ),
        );
    };

    const handleSelectModuleType = (value: string) => {
        dispatch(
            setExperienceFilters(
                withModuleTypeUpdate(experienceFilters, [value]),
            ),
        );
        setTypeAnchorEl(null);
    };

    const handleClearModuleType = () => {
        dispatch(
            setExperienceFilters(withModuleTypeUpdate(experienceFilters, [])),
        );
        setTypeAnchorEl(null);
    };

    const handleVisitType = (type: VisitType) => {
        setActiveVisitType(type);
        localStorage.setItem('visitTypePref', type);
        navigate(`/viewExperiences/${type}`);
    };

    const handleWishlistFilterToggle = () => {
        dispatch(
            setExperienceFilters({
                ...experienceFilters,
                wishlist: !isWishlistOnly,
            }),
        );
        dispatch(sevenDayApi.util.invalidateTags(['Assets', 'Wishlist']));
    };

    const isGuestUser = loggedInUser?.role?.includes('guest');
    const isOwnerOrAdmin =
        loggedInUser?.role.includes('owner') || loggedInUser?.role === 'admin';

    return (
        <Grid
            container
            sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                pb: 2,
            }}
        >
            <style>{getCss()}</style>
            <Typography component={'h1'} variant='visuallyHidden'>
                View all listings
            </Typography>
            <Grid item xs={12} sx={{ maxWidth: 1400, width: '100%' }}>
                <Grid
                    sx={{
                        position: 'sticky',
                        top: 0,
                        left: 0,
                        right: 0,
                        zIndex: 1000,
                        bgcolor: theme.palette.background.default,
                        mt: -20,
                        pt: isOwnerOrAdmin && !isIpad ? 5 : 10,
                        pb: 0.1,
                    }}
                >
                    <Stack
                        direction='row'
                        spacing={1.5}
                        justifyContent='center'
                        alignItems='center'
                        sx={{ py: 3 }}
                    >
                        <Typography component={'h2'} variant='visuallyHidden'>
                            Search filter
                        </Typography>
                        <ToggleButtonGroup
                            value={activeVisitType ?? null}
                            exclusive
                            onChange={(_, v) => v && handleVisitType(v)}
                            sx={{
                                '& .MuiToggleButton-root': {
                                    textTransform: 'none',
                                    px: 2,
                                    height: 36,
                                    borderRadius: 2,
                                },
                                '& .Mui-selected': {
                                    bgcolor: 'primary.main',
                                    color: 'secondary.main',
                                    pointerEvents: 'none',
                                },
                                '& .MuiToggleButton-root:not(.Mui-selected):hover':
                                    {
                                        bgcolor: 'secondary.main',
                                        color: 'primary.main',
                                    },
                                maxHeight: 36,
                            }}
                        >
                            <ToggleButton disableRipple value='business'>
                                Business
                            </ToggleButton>
                            <ToggleButton disableRipple value='personal'>
                                Personal
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Stack>

                    {isMobile ? null : (
                        <Paper
                            elevation={1}
                            sx={{
                                border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                                borderRadius: 2,
                                px: 2,
                                py: 1.75,
                                mb: 3,
                                width: '100%',
                                maxWidth: 1040,
                                mx: 'auto',
                            }}
                        >
                            <Box id='guest-experience-search-panel'>
                                <Stack
                                    direction='row'
                                    spacing={1.5}
                                    alignItems='center'
                                    sx={{ width: '100%' }}
                                >
                                    <Stack
                                        direction='row'
                                        spacing={1}
                                        alignItems='center'
                                        sx={{
                                            width: '100%',
                                            pl: { xs: 0, md: 1 },
                                            flex: 1,
                                            minWidth: { xs: 0, md: 280 },
                                        }}
                                    >
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <AddressSearch
                                                onChange={handleAddress}
                                                hideSearchIcon={isMobile}
                                            />
                                        </Box>
                                    </Stack>

                                    <Box
                                        sx={{
                                            width: { xs: '100%', md: 'auto' },
                                            flexShrink: 0,
                                        }}
                                    >
                                        <DateFilter />
                                    </Box>

                                    <Box
                                        sx={{
                                            width: { xs: '100%', md: 190 },
                                            flexShrink: 0,
                                        }}
                                    >
                                        <Typography
                                            variant='body2'
                                            sx={{
                                                fontWeight: 'medium',
                                                color: theme.typography.body1
                                                    .color,
                                                mb: 0.35,
                                            }}
                                        >
                                            Type of experience
                                        </Typography>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.25,
                                                width: '100%',
                                            }}
                                        >
                                            <ButtonBase
                                                disableRipple
                                                disableTouchRipple
                                                aria-label='Type of experience'
                                                aria-haspopup='listbox'
                                                aria-expanded={typePopoverOpen}
                                                onClick={(e) =>
                                                    setTypeAnchorEl(
                                                        e.currentTarget,
                                                    )
                                                }
                                                sx={{
                                                    flex: 1,
                                                    minWidth: 0,
                                                    border: 'none',
                                                    borderRadius: 1.5,
                                                    px: 0,
                                                    py: 0.6,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent:
                                                        'flex-start',
                                                    gap: 1,
                                                    backgroundColor:
                                                        'transparent',
                                                    transition: 'none',
                                                    '&:hover': {
                                                        backgroundColor:
                                                            'transparent',
                                                    },
                                                }}
                                            >
                                                <Typography
                                                    component='span'
                                                    variant={
                                                        isMobile
                                                            ? 'body2'
                                                            : 'caption'
                                                    }
                                                    sx={{
                                                        color: activeModuleType
                                                            ? theme.palette.text
                                                                  .primary
                                                            : theme.palette.text
                                                                  .secondary,
                                                        fontWeight: 400,
                                                        overflow: 'hidden',
                                                        textOverflow:
                                                            'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {activeModuleTypeLabel}
                                                </Typography>
                                            </ButtonBase>
                                            {activeModuleType ? (
                                                <CustomIconButton
                                                    onClick={
                                                        handleClearModuleType
                                                    }
                                                    disableRipple
                                                    aria-label='Clear type of experience'
                                                    sx={{
                                                        flexShrink: 0,
                                                        p: 0.25,
                                                        color: theme.palette
                                                            .text.secondary,
                                                    }}
                                                >
                                                    <Clear
                                                        sx={{ fontSize: 12 }}
                                                    />
                                                </CustomIconButton>
                                            ) : null}
                                        </Box>

                                        <Popover
                                            open={typePopoverOpen}
                                            anchorEl={typeAnchorEl}
                                            onClose={() =>
                                                setTypeAnchorEl(null)
                                            }
                                            anchorOrigin={{
                                                vertical: 'bottom',
                                                horizontal: 'left',
                                            }}
                                            transformOrigin={{
                                                vertical: 'top',
                                                horizontal: 'left',
                                            }}
                                            PaperProps={{
                                                sx: {
                                                    mt: 1,
                                                    p: 1.25,
                                                    borderRadius: 2,
                                                    border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                                                    width: 340,
                                                    maxWidth:
                                                        'calc(100vw - 24px)',
                                                },
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'grid',
                                                    gridTemplateColumns:
                                                        'repeat(3, minmax(0, 1fr))',
                                                    gap: 1,
                                                }}
                                            >
                                                {MODULE_TYPE_CHIPS.map(
                                                    (chip) => (
                                                        <ButtonBase
                                                            key={chip.value}
                                                            aria-label={
                                                                chip.label
                                                            }
                                                            aria-pressed={
                                                                activeModuleType ===
                                                                chip.value
                                                            }
                                                            onClick={() =>
                                                                handleSelectModuleType(
                                                                    chip.value,
                                                                )
                                                            }
                                                            sx={{
                                                                borderRadius: 2,
                                                                border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                                                                p: 1,
                                                                display: 'flex',
                                                                flexDirection:
                                                                    'column',
                                                                alignItems:
                                                                    'center',
                                                                gap: 0.5,
                                                                backgroundColor:
                                                                    activeModuleType ===
                                                                    chip.value
                                                                        ? theme
                                                                              .palette
                                                                              .action
                                                                              .hover
                                                                        : theme
                                                                              .palette
                                                                              .background
                                                                              .paper,
                                                                '&:hover': {
                                                                    backgroundColor:
                                                                        theme
                                                                            .palette
                                                                            .action
                                                                            .hover,
                                                                },
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    color: 'text.secondary',
                                                                    display:
                                                                        'flex',
                                                                    alignItems:
                                                                        'center',
                                                                }}
                                                            >
                                                                {chip.icon}
                                                            </Box>
                                                            <Typography
                                                                variant='caption'
                                                                sx={{
                                                                    fontWeight: 500,
                                                                    color: 'text.primary',
                                                                }}
                                                            >
                                                                {chip.label}
                                                            </Typography>
                                                        </ButtonBase>
                                                    ),
                                                )}
                                            </Box>

                                            <Box
                                                sx={{
                                                    mt: 1.25,
                                                    display: 'flex',
                                                    justifyContent: 'flex-end',
                                                }}
                                            >
                                                <ButtonBase
                                                    onClick={
                                                        handleClearModuleType
                                                    }
                                                    sx={{
                                                        typography: 'body2',
                                                        px: 1.25,
                                                        py: 0.75,
                                                        borderRadius: 2,
                                                        color: 'text.secondary',
                                                        '&:hover': {
                                                            backgroundColor:
                                                                theme.palette
                                                                    .action
                                                                    .hover,
                                                        },
                                                    }}
                                                >
                                                    Clear
                                                </ButtonBase>
                                            </Box>
                                        </Popover>
                                    </Box>

                                    <Box
                                        sx={{
                                            alignSelf: 'stretch',
                                            borderLeft: `1px solid ${theme.palette.faded.main}`,
                                            height: 'auto',
                                            paddingRight: '20px',
                                        }}
                                    />
                                    <Box
                                        sx={{
                                            minWidth: 'auto',
                                            flexShrink: 0,
                                        }}
                                    >
                                        <Stack
                                            spacing={0.75}
                                            alignItems='center'
                                            sx={{ py: 0.25 }}
                                        >
                                            {isGuestUser ? (
                                                <ButtonBase
                                                    onClick={
                                                        handleWishlistFilterToggle
                                                    }
                                                    disableRipple
                                                    aria-label='Favourites only'
                                                >
                                                    {isWishlistOnly ? (
                                                        <FavoriteIcon
                                                            sx={{
                                                                fontSize: 18,
                                                                color: 'error.main',
                                                            }}
                                                        />
                                                    ) : (
                                                        <FavoriteBorderIcon
                                                            sx={{
                                                                fontSize: 18,
                                                                color: 'text.secondary',
                                                            }}
                                                        />
                                                    )}
                                                </ButtonBase>
                                            ) : null}
                                            <FiltersModal management={false} />
                                        </Stack>
                                    </Box>
                                </Stack>
                            </Box>
                        </Paper>
                    )}
                </Grid>

                <Grid container>
                    <Typography component={'h2'} variant='visuallyHidden'>
                        Experiences
                    </Typography>
                    <Grid
                        item
                        xs={12}
                        sx={{
                            pb: isMobile ? 12 : isIpad ? 5 : 0,
                        }}
                    >
                        <ExperiencesListAllComponent
                            visitType={activeVisitType}
                        />
                    </Grid>
                </Grid>
            </Grid>

            <VisitTypeModal
                open={openVisitModal}
                onClose={handleCloseVisitModal}
                heroTitle='Choose how you want to explore'
                heroSubtitle='Tailored experiences for teams and individuals'
                ctaBusiness='Explore'
                ctaPersonal='Explore'
                illustration={
                    <Box
                        component='img'
                        src='/illustrations/rocketicon.png'
                        alt=''
                        width={88}
                        height={88}
                    />
                }
            />

            {loggedInUser?.role === 'external_attendee_user' && (
                <AccessDeniedModal />
            )}

            {isMobile && (
                <>
                    <Box
                        sx={{
                            position: 'fixed',
                            left: 0,
                            right: 0,
                            bottom: 0,
                            zIndex: 1200,
                            px: 2,
                            pt: 1.5,
                            pb: `calc(env(safe-area-inset-bottom, 0px) + 12px)`,
                            background: `linear-gradient(180deg, rgba(255,255,255,0) 0%, ${theme.palette.background.default} 35%)`,
                            pointerEvents: 'none',
                            display: 'flex',
                            justifyContent: 'center',
                        }}
                    >
                        <Button
                            onClick={() => setMobileSheetOpen(true)}
                            startIcon={<SearchIcon />}
                            sx={{
                                pointerEvents: 'auto',
                                py: 1.25,
                                px: 3,
                                borderRadius: 999,
                                bgcolor: 'primary.main',
                                color: 'common.white',
                                textTransform: 'none',
                                fontSize: 15,
                                fontWeight: 600,
                                boxShadow: theme.shadows[6],
                                '&:hover': { bgcolor: 'primary.dark' },
                            }}
                        >
                            Search Experiences
                        </Button>
                    </Box>

                    <SwipeableDrawer
                        anchor='bottom'
                        open={mobileSheetOpen}
                        onClose={() => setMobileSheetOpen(false)}
                        onOpen={() => setMobileSheetOpen(true)}
                        disableSwipeToOpen
                        PaperProps={{
                            sx: {
                                borderTopLeftRadius: 16,
                                borderTopRightRadius: 16,
                                maxHeight: '92vh',
                                pb: `calc(env(safe-area-inset-bottom, 0px) + 12px)`,
                            },
                        }}
                    >
                        <Box
                            sx={{
                                width: 36,
                                height: 4,
                                bgcolor: 'grey.300',
                                borderRadius: 4,
                                mx: 'auto',
                                mt: 1,
                                mb: 1.5,
                                flexShrink: 0,
                            }}
                        />
                        <Box
                            sx={{
                                px: 2,
                                pb: 2,
                                overflowY: 'auto',
                                flex: 1,
                            }}
                        >
                            <Typography
                                variant='h5'
                                sx={{ fontWeight: 700, mb: 2 }}
                            >
                                Search
                            </Typography>

                            <Box sx={{ mb: 1.5 }}>
                                <AddressSearch
                                    onChange={handleAddress}
                                    hideSearchIcon={false}
                                />
                            </Box>

                            <Divider sx={{ my: 1.5 }} />

                            <Box sx={{ mb: 1.5 }}>
                                <DateFilter />
                            </Box>

                            <Divider sx={{ my: 1.5 }} />

                            <Typography
                                variant='body2'
                                sx={{
                                    fontWeight: 'medium',
                                    color: theme.typography.body1.color,
                                    mb: 0.75,
                                }}
                            >
                                Type of experience
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 1,
                                    overflowX: 'auto',
                                    pb: 0.5,
                                    mb: 2,
                                    '&::-webkit-scrollbar': {
                                        display: 'none',
                                    },
                                }}
                            >
                                {MODULE_TYPE_CHIPS.map((chip) => {
                                    const selected =
                                        activeModuleType === chip.value;
                                    return (
                                        <ButtonBase
                                            key={chip.value}
                                            onClick={() =>
                                                selected
                                                    ? handleClearModuleType()
                                                    : handleSelectModuleType(
                                                          chip.value,
                                                      )
                                            }
                                            sx={{
                                                flexShrink: 0,
                                                borderRadius: 2,
                                                border: `1px solid ${
                                                    selected
                                                        ? theme.palette.primary
                                                              .main
                                                        : theme.palette.divider
                                                }`,
                                                px: 1.5,
                                                py: 0.75,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.75,
                                                bgcolor: selected
                                                    ? 'action.selected'
                                                    : 'background.paper',
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    color: 'text.secondary',
                                                    display: 'flex',
                                                }}
                                            >
                                                {chip.icon}
                                            </Box>
                                            <Typography
                                                variant='body2'
                                                sx={{ fontWeight: 600 }}
                                            >
                                                {chip.label}
                                            </Typography>
                                        </ButtonBase>
                                    );
                                })}
                            </Box>

                            <Divider sx={{ my: 1.5 }} />

                            {isGuestUser ? (
                                <ButtonBase
                                    onClick={handleWishlistFilterToggle}
                                    sx={{
                                        width: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        borderRadius: 2,
                                        border: `1px solid ${
                                            isWishlistOnly
                                                ? theme.palette.error.main
                                                : theme.palette.divider
                                        }`,
                                        px: 1.5,
                                        py: 1,
                                        mb: 2,
                                        bgcolor: isWishlistOnly
                                            ? 'error.light'
                                            : 'background.paper',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                        }}
                                    >
                                        {isWishlistOnly ? (
                                            <FavoriteIcon
                                                sx={{
                                                    fontSize: 18,
                                                    color: 'error.main',
                                                }}
                                            />
                                        ) : (
                                            <FavoriteBorderIcon
                                                sx={{
                                                    fontSize: 18,
                                                    color: 'text.secondary',
                                                }}
                                            />
                                        )}
                                        <Typography
                                            variant='body2'
                                            sx={{
                                                fontWeight: 600,
                                                color: isWishlistOnly
                                                    ? 'error.main'
                                                    : 'text.primary',
                                            }}
                                        >
                                            Favourites only
                                        </Typography>
                                    </Box>
                                    <Typography
                                        variant='caption'
                                        sx={{ color: 'text.secondary' }}
                                    >
                                        {isWishlistOnly ? 'On' : 'Off'}
                                    </Typography>
                                </ButtonBase>
                            ) : null}

                            <ButtonBase
                                onClick={() => setMobileFiltersOpen(true)}
                                sx={{
                                    width: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    py: 0.5,
                                    mb: 2,
                                    textAlign: 'left',
                                }}
                            >
                                <Typography
                                    variant='body2'
                                    sx={{
                                        fontWeight: 'medium',
                                        color: theme.typography.body1.color,
                                    }}
                                >
                                    More filters
                                </Typography>
                                <ChevronRightIcon
                                    fontSize='small'
                                    sx={{ color: 'text.secondary' }}
                                />
                            </ButtonBase>

                            <Box sx={{ display: 'none' }}>
                                <FiltersModal
                                    management={false}
                                    open={mobileFiltersOpen}
                                    onOpenChange={setMobileFiltersOpen}
                                    hideTrigger
                                />
                            </Box>

                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                }}
                            >
                                <Button
                                    onClick={() => setMobileSheetOpen(false)}
                                    startIcon={<SearchIcon />}
                                    sx={{
                                        py: 1.25,
                                        px: 3,
                                        borderRadius: 999,
                                        bgcolor: 'primary.main',
                                        color: 'common.white',
                                        textTransform: 'none',
                                        fontSize: 15,
                                        fontWeight: 600,
                                        '&:hover': { bgcolor: 'primary.dark' },
                                    }}
                                >
                                    Search Experiences
                                </Button>
                            </Box>
                        </Box>
                    </SwipeableDrawer>
                </>
            )}
        </Grid>
    );
}
